import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { runDocumentChat } from '@/lib/documentAi';
import { extractJsonObject, parseAiPayload, type AiChartSpec } from '@/lib/aiSheet';
import {
  parseDeckFromAi,
  slidesFromPlainText,
  type DeckChart,
  type DeckSlide,
  type DeckTheme,
} from '@/lib/slideDeck';
import {
  deleteChatSession,
  listChatSessions,
  makeSessionId,
  relativeSessionDate,
  saveChatSession,
  sessionTitle,
  type EditorChatMessage,
  type EditorChatSession,
} from '@/lib/editorChatHistory';

interface PresentationAiPanelProps {
  open: boolean;
  theme: DeckTheme;
  slideCount: number;
  currentSlide: number;
  historyKey?: string;
  getContext: () => string;
  onClose: () => void;
  onApplySlides: (slides: DeckSlide[], mode: 'replace' | 'append' | 'edit', targetIndex?: number) => void;
  onAddChart: (targetIndex: number, chart: DeckChart) => void;
  onAddTextSlide: (title: string, body: string) => void;
}

const SUGGESTIONS = [
  'Fais-moi la présentation du site https://zifek.fr',
  'Crée une présentation sur la transition énergétique en Afrique',
  'Ajoute un graphique des ventes par secteur dans la diapo 3',
  'Ajoute une diapositive de conclusion avec les prochaines étapes',
];

interface ParsedReply {
  text: string;
  slides: DeckSlide[];
  chart: AiChartSpec | null;
  target: number | null;
  mode: 'replace' | 'append' | 'edit';
}

function parseReply(raw: string, theme: DeckTheme): ParsedReply {
  const base = parseAiPayload(raw);
  const json = extractJsonObject(raw);
  let slides: DeckSlide[] = [];
  let target: number | null = null;
  let mode: 'replace' | 'append' | 'edit' = 'append';
  if (json) {
    try {
      const obj = JSON.parse(json) as Record<string, unknown>;
      slides = parseDeckFromAi(obj);
      const rawMode = String(obj.mode || obj.action || '').toLowerCase();
      if (rawMode.includes('replace') || rawMode.includes('remplac')) mode = 'replace';
      else if (rawMode.includes('edit') || rawMode.includes('modif')) mode = 'edit';
      const t = Number(obj.targetSlide ?? obj.slide ?? obj.target);
      if (Number.isFinite(t) && t > 0) target = Math.floor(t) - 1;
      const reply = obj.reply ?? obj.message ?? obj.note;
      if (typeof reply === 'string' && reply.trim()) {
        return { text: reply.trim(), slides, chart: base.chart, target, mode };
      }
    } catch {
      /* repli sur le texte */
    }
  }
  if (!slides.length && base.text && base.text.length > 120) {
    slides = slidesFromPlainText(base.text, theme);
  }
  return { text: base.text || base.note || '', slides, chart: base.chart, target, mode };
}

export default function PresentationAiPanel({
  open,
  theme,
  slideCount,
  currentSlide,
  historyKey,
  getContext,
  onClose,
  onApplySlides,
  onAddChart,
  onAddTextSlide,
}: PresentationAiPanelProps) {
  const [messages, setMessages] = useState<EditorChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [width, setWidth] = useState(390);
  const [minimized, setMinimized] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [sessions, setSessions] = useState<EditorChatSession[]>([]);
  const [sessionId, setSessionId] = useState(() => makeSessionId());
  const [chartTarget, setChartTarget] = useState(currentSlide + 1);
  const [appliedKey, setAppliedKey] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const resizingRef = useRef(false);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  useEffect(() => {
    setChartTarget(currentSlide + 1);
  }, [currentSlide]);

  useEffect(() => {
    if (!historyKey) return;
    setSessions(listChatSessions(historyKey));
  }, [historyKey, open]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizingRef.current) return;
      setWidth(Math.min(780, Math.max(300, window.innerWidth - e.clientX)));
    };
    const onUp = () => {
      resizingRef.current = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const persist = (next: EditorChatMessage[]) => {
    if (!historyKey || !next.length) return;
    const now = Date.now();
    const existing = listChatSessions(historyKey).find((s) => s.id === sessionId);
    setSessions(
      saveChatSession(historyKey, {
        id: sessionId,
        title: sessionTitle(next),
        mode: 'slides',
        createdAt: existing?.createdAt || now,
        updatedAt: now,
        messages: next,
      }),
    );
  };

  const send = async (prompt?: string) => {
    const question = (prompt ?? input).trim();
    if (!question || loading) return;
    setInput('');
    setError(null);
    const next: EditorChatMessage[] = [...messages, { role: 'user', content: question }];
    setMessages(next);
    setLoading(true);
    try {
      const result = await runDocumentChat({
        mode: 'slides',
        message: question,
        context: getContext(),
        // L'historique est volontairement tronqué : renvoyer l'intégralité des
        // réponses JSON (très longues) faisait échouer la fonction Backend.
        history: messages
          .slice(-4)
          .map((m) => ({
            role: m.role === 'assistant' ? ('assistant' as const) : ('user' as const),
            content: m.content.slice(0, 1200),
          })),
      });
      const updated: EditorChatMessage[] = [...next, { role: 'assistant', content: result.result }];
      setMessages(updated);
      persist(updated);
    } catch (err) {
      const raw = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(
        /non-2xx|Failed to send|non-2xx status/i.test(raw)
          ? "L'assistant n'a pas pu répondre (requête trop lourde ou service momentanément indisponible). Réessayez — la présentation sera créée même sans historique."
          : raw,
      );
    } finally {
      setLoading(false);
    }
  };

  const resetConversation = () => {
    setMessages([]);
    setError(null);
    setSessionId(makeSessionId());
    setAppliedKey(null);
    setShowHistory(false);
  };

  const loadSession = (session: EditorChatSession) => {
    setMessages(session.messages);
    setSessionId(session.id);
    setShowHistory(false);
    setAppliedKey(null);
  };

  const removeSession = (id: string) => {
    if (!historyKey) return;
    setSessions(deleteChatSession(historyKey, id));
    if (id === sessionId) resetConversation();
  };

  const parsedList = useMemo(
    () => messages.map((m) => (m.role === 'assistant' ? parseReply(m.content, theme) : null)),
    [messages, theme],
  );

  if (!open) return null;

  if (minimized) {
    return (
      <div className="flex flex-col items-center gap-2 w-14 flex-shrink-0 border-l border-background-200/70 bg-background-50 py-3">
        <button
          onClick={() => setMinimized(false)}
          className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center cursor-pointer hover:bg-accent-200/70"
          title="Agrandir l'assistant"
        >
          <i className="ri-sparkling-line"></i>
        </button>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground-400" style={{ writingMode: 'vertical-rl' }}>
          Assistant IA
        </span>
        <button onClick={onClose} className="mt-auto w-9 h-9 rounded-full text-foreground-400 hover:bg-background-100 flex items-center justify-center cursor-pointer" title="Fermer">
          <i className="ri-close-line"></i>
        </button>
      </div>
    );
  }

  return (
    <div
      className="relative flex flex-col flex-shrink-0 border-l border-background-200/70 bg-background-50"
      style={{ width }}
    >
      <div
        onMouseDown={(e: ReactMouseEvent) => {
          e.preventDefault();
          resizingRef.current = true;
        }}
        className="absolute left-0 top-0 bottom-0 w-1.5 -ml-0.5 cursor-col-resize hover:bg-accent-300/60 transition-colors"
        title="Glisser pour redimensionner"
      ></div>

      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-background-200/70">
        <span className="w-7 h-7 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-sparkling-line text-sm"></i>
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground-950 truncate">Assistant IA</p>
          <p className="text-[10px] text-foreground-400 truncate">Crée et modifie les diapositives</p>
        </div>
        <button
          onClick={() => {
            setShowHistory((v) => !v);
            if (historyKey) setSessions(listChatSessions(historyKey));
          }}
          className={`w-7 h-7 rounded-full flex items-center justify-center cursor-pointer ${showHistory ? 'bg-accent-100 text-accent-700' : 'text-foreground-400 hover:bg-background-100'}`}
          title="Historique des conversations"
        >
          <i className="ri-history-line text-sm"></i>
        </button>
        <button onClick={resetConversation} className="w-7 h-7 rounded-full text-foreground-400 hover:bg-background-100 flex items-center justify-center cursor-pointer" title="Nouvelle conversation">
          <i className="ri-add-line text-sm"></i>
        </button>
        <button onClick={() => setMinimized(true)} className="w-7 h-7 rounded-full text-foreground-400 hover:bg-background-100 flex items-center justify-center cursor-pointer" title="Réduire">
          <i className="ri-subtract-line text-sm"></i>
        </button>
        <button onClick={onClose} className="w-7 h-7 rounded-full text-foreground-400 hover:bg-background-100 flex items-center justify-center cursor-pointer" title="Fermer">
          <i className="ri-close-line text-sm"></i>
        </button>
      </div>

      {showHistory ? (
        <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1.5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-foreground-800">Conversations enregistrées</p>
            <button onClick={resetConversation} className="text-[11px] text-primary-600 hover:underline cursor-pointer whitespace-nowrap">
              Nouvelle
            </button>
          </div>
          {sessions.length === 0 && (
            <p className="text-xs text-foreground-400 py-6 text-center">Aucune conversation enregistrée pour ce document.</p>
          )}
          {sessions.map((s) => (
            <div
              key={s.id}
              className={`group flex items-start gap-2 px-2.5 py-2 rounded-lg border cursor-pointer transition-colors ${
                s.id === sessionId ? 'border-accent-300 bg-accent-50/70' : 'border-background-200/70 bg-white hover:bg-background-100'
              }`}
              onClick={() => loadSession(s)}
            >
              <i className="ri-chat-3-line text-foreground-400 mt-0.5"></i>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-foreground-800 truncate">{s.title}</p>
                <p className="text-[10px] text-foreground-400">
                  {relativeSessionDate(s.updatedAt)} · {s.messages.length} messages
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeSession(s.id);
                }}
                className="opacity-0 group-hover:opacity-100 w-6 h-6 rounded-full text-foreground-400 hover:text-red-500 hover:bg-red-50 flex items-center justify-center cursor-pointer"
                title="Supprimer"
              >
                <i className="ri-delete-bin-line text-xs"></i>
              </button>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-base font-semibold text-foreground-800 leading-snug">
                  Que dois-je préparer pour votre présentation ?
                </p>
                <p className="text-[11px] text-foreground-500 leading-relaxed">
                  Donnez un sujet, un lien à présenter, ou demandez un graphique sur une diapositive précise.
                </p>
                <div className="space-y-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => void send(s)}
                      className="w-full flex items-start gap-2 px-2.5 py-2 rounded-lg text-left text-xs text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors"
                    >
                      <i className="ri-corner-down-right-line text-foreground-400 mt-0.5 flex-shrink-0"></i>
                      <span>{s}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, index) => {
              if (m.role === 'user') {
                return (
                  <div key={index} className="flex justify-end">
                    <div className="max-w-[85%] px-3 py-2 rounded-lg bg-primary-500 text-background-50 text-xs whitespace-pre-wrap">
                      {m.content}
                    </div>
                  </div>
                );
              }
              const parsed = parsedList[index];
              if (!parsed) return null;
              const keyBase = `${index}`;
              return (
                <div key={index} className="space-y-2">
                  {parsed.text && (
                    <div className="px-3 py-2 rounded-lg bg-white border border-background-200/70 text-xs text-foreground-800 whitespace-pre-wrap">
                      {parsed.text}
                    </div>
                  )}
                  {parsed.slides.length > 0 && (
                    <div className="rounded-lg border border-background-200/70 bg-white p-2.5 space-y-2">
                      <p className="text-[11px] font-semibold text-foreground-800 flex items-center gap-1.5">
                        <i className="ri-layout-masonry-line text-accent-600"></i>
                        {parsed.slides.length} diapositive{parsed.slides.length > 1 ? 's' : ''} prête{parsed.slides.length > 1 ? 's' : ''}
                      </p>
                      <ul className="space-y-0.5 max-h-28 overflow-y-auto">
                        {parsed.slides.map((s, i) => (
                          <li key={s.id} className="text-[11px] text-foreground-600 truncate">
                            {i + 1}. {s.title || 'Sans titre'}
                          </li>
                        ))}
                      </ul>
                      <button
                        onClick={() => {
                          onApplySlides(parsed.slides, parsed.mode === 'replace' ? 'replace' : 'append');
                          setAppliedKey(`${keyBase}-deck`);
                        }}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-accent-500 text-background-50 text-[11px] font-medium hover:bg-accent-600 cursor-pointer"
                      >
                        <i className={appliedKey === `${keyBase}-deck` ? 'ri-check-line' : 'ri-add-box-line'}></i>
                        {appliedKey === `${keyBase}-deck`
                          ? 'Ajouté à la présentation'
                          : parsed.mode === 'replace'
                            ? 'Remplacer la présentation'
                            : 'Ajouter à la présentation'}
                      </button>
                      {slideCount > 0 && (
                        <button
                          onClick={() => {
                            const target = (parsed.target ?? currentSlide);
                            onApplySlides(parsed.slides, 'edit', target);
                            setAppliedKey(`${keyBase}-edit`);
                          }}
                          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-foreground-700 text-[11px] font-medium hover:bg-background-100 cursor-pointer"
                        >
                          <i className="ri-edit-line"></i>
                          Remplacer la diapositive {Math.min(slideCount, (parsed.target ?? currentSlide) + 1)}
                        </button>
                      )}
                    </div>
                  )}
                  {parsed.chart && (
                    <div className="rounded-lg border border-background-200/70 bg-white p-2.5 space-y-2">
                      <p className="text-[11px] font-semibold text-foreground-800">{parsed.chart.title}</p>
                      <div className="flex items-center gap-2">
                        <label className="text-[10px] text-foreground-500 whitespace-nowrap">Diapositive</label>
                        <input
                          type="number"
                          min={1}
                          max={Math.max(1, slideCount)}
                          value={chartTarget}
                          onChange={(e) => setChartTarget(Number(e.target.value) || 1)}
                          className="w-16 px-2 py-1 rounded-md border border-background-200/70 text-xs"
                        />
                        <button
                          onClick={() => {
                            onAddChart(Math.min(Math.max(1, chartTarget), Math.max(1, slideCount)) - 1, {
                              kind: parsed.chart!.kind,
                              title: parsed.chart!.title,
                              categories: parsed.chart!.categories,
                              values: parsed.chart!.series[0]?.values || [],
                            });
                            setAppliedKey(`${keyBase}-chart`);
                          }}
                          className="flex-1 px-3 py-1.5 rounded-md bg-secondary-500 text-background-50 text-[11px] font-medium hover:bg-secondary-600 cursor-pointer whitespace-nowrap"
                        >
                          <i className={appliedKey === `${keyBase}-chart` ? 'ri-check-line mr-1' : 'ri-bar-chart-box-line mr-1'}></i>
                          Insérer le graphique
                        </button>
                      </div>
                    </div>
                  )}
                  {!parsed.slides.length && !parsed.chart && parsed.text && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          onAddTextSlide('Diapositive', parsed.text);
                          setAppliedKey(`${keyBase}-text`);
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent-500 text-background-50 text-[11px] font-medium hover:bg-accent-600 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-add-box-line"></i>
                        En faire une diapositive
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {loading && (
              <div className="flex items-center gap-2 text-xs text-foreground-500">
                <i className="ri-loader-4-line animate-spin"></i>
                L'assistant prépare la présentation…
              </div>
            )}
            {error && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-[11px]">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span className="flex-1">{error}</span>
              </div>
            )}
          </div>

          <div className="border-t border-background-200/70 p-2.5">
            <div className="rounded-xl border border-background-200/70 bg-white px-2.5 py-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void send();
                  }
                }}
                rows={2}
                maxLength={2000}
                placeholder="Ex : mets un graphique des ventes dans la diapo 5…"
                className="w-full resize-none bg-transparent text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              <div className="flex items-center justify-between pt-1">
                <span className="text-[10px] text-foreground-300">Entrée pour envoyer</span>
                <button
                  onClick={() => void send()}
                  disabled={!input.trim() || loading}
                  className="w-7 h-7 rounded-full bg-accent-500 text-background-50 flex items-center justify-center disabled:opacity-40 cursor-pointer hover:bg-accent-600"
                  title="Envoyer"
                >
                  <i className="ri-arrow-up-line text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}